import { useEffect, useState } from 'react';
import { klienApi } from '../api/klien';
import { ApiException } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { Card, AccentCard } from '../components/Card';
import { Input } from '../components/Input';
import { AppLayout } from '../components/Layout';
import type { KlienMeResponse } from '../types/api';

export function ProfilPage() {
  const { refreshKlien } = useAuth();
  const [me, setMe] = useState<KlienMeResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    klienApi
      .me()
      .then(setMe)
      .catch((err) => {
        setLoadError(err instanceof ApiException ? err.message : 'Gagal memuat profil.');
      });
  }, []);

  return (
    <AppLayout>
      <AccentCard
        title="Profil Saya"
        subtitle="Kelola data akun klien dan keamanan login portal"
      >
        {loadError && <Alert variant="error">{loadError}</Alert>}
      </AccentCard>

      <div className="mt-6 space-y-6">
        {me && (
          <>
            <InfoCard me={me} />
            <EditProfilForm me={me} onSaved={async () => {
              const updated = await klienApi.me();
              setMe(updated);
              await refreshKlien();
            }} />
            <ChangePasswordForm />
          </>
        )}
      </div>
    </AppLayout>
  );
}

// =============================================================================

function InfoCard({ me }: { me: KlienMeResponse }) {
  return (
    <Card title="Informasi Akun" subtitle="Data tetap — tidak bisa diubah">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Kode Klien" value={me.kodeKlien} mono />
        <Field label="Status Akun">
          <StatusBadge status={me.status} />
        </Field>
        <Field label="Username" value={`@${me.username}`} mono />
        <Field label="Email" value={me.email} />
        <Field label="Tanggal Daftar" value={formatDateTime(me.tanggalDaftar)} />
        <Field label="Verifikasi Email" value={formatDateTime(me.tanggalVerifikasiEmail) || '—'} />
        <Field label="Tanggal Approval" value={formatDateTime(me.tanggalApproval) || '—'} />
        <Field label="Login Terakhir" value={formatDateTime(me.tanggalTerakhirLogin) || '—'} />
      </div>
    </Card>
  );
}

// =============================================================================

function EditProfilForm({
  me,
  onSaved,
}: {
  me: KlienMeResponse;
  onSaved: () => Promise<void>;
}) {
  const [namaLengkap, setNamaLengkap] = useState(me.namaLengkap);
  const [nomorHp, setNomorHp] = useState(me.nomorHp ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const isDirty = namaLengkap.trim() !== me.namaLengkap || nomorHp.trim() !== (me.nomorHp ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setSubmitting(true);
    try {
      await klienApi.updateMe({ namaLengkap: namaLengkap.trim(), nomorHp: nomorHp.trim() });
      await onSaved();
      setMessage({ kind: 'success', text: 'Profil berhasil diperbarui.' });
    } catch (err) {
      setMessage({
        kind: 'error',
        text: err instanceof ApiException ? err.message : 'Gagal memperbarui profil.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="Edit Profil" subtitle="Ubah nama lengkap dan nomor HP">
      <form onSubmit={handleSubmit} className="space-y-4">
        {message && <Alert variant={message.kind === 'success' ? 'success' : 'error'}>{message.text}</Alert>}
        <Input
          label="Nama Lengkap"
          name="namaLengkap"
          value={namaLengkap}
          onChange={(e) => setNamaLengkap(e.target.value)}
          required
          maxLength={100}
        />
        <Input
          label="Nomor HP"
          name="nomorHp"
          value={nomorHp}
          onChange={(e) => setNomorHp(e.target.value)}
          maxLength={20}
          hint="Format bebas (contoh: 08123456789 atau +62812-3456-7890)"
        />
        <div className="flex justify-end pt-2">
          <Button type="submit" variant="primary" disabled={!isDirty || submitting}>
            {submitting ? 'Menyimpan...' : 'Simpan Perubahan'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// =============================================================================

function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const validate = (): string | null => {
    if (!currentPassword) return 'Password lama wajib diisi.';
    if (newPassword.length < 8) return 'Password baru minimal 8 karakter.';
    if (newPassword !== confirmNewPassword) return 'Konfirmasi password baru tidak cocok.';
    if (newPassword === currentPassword) return 'Password baru harus berbeda dari password lama.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    const err = validate();
    if (err) {
      setMessage({ kind: 'error', text: err });
      return;
    }

    setSubmitting(true);
    try {
      await klienApi.changePassword({ currentPassword, newPassword });
      setMessage({ kind: 'success', text: 'Password berhasil diubah.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setMessage({
        kind: 'error',
        text: err instanceof ApiException ? err.message : 'Gagal mengubah password.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="Ubah Password" subtitle="Password klien untuk login portal Booku Client">
      <form onSubmit={handleSubmit} className="space-y-4">
        {message && (
          <Alert variant={message.kind === 'success' ? 'success' : 'error'}>{message.text}</Alert>
        )}
        <Input
          label="Password Lama"
          type="password"
          name="currentPassword"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
        <Input
          label="Password Baru"
          type="password"
          name="newPassword"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          autoComplete="new-password"
          hint="Minimal 8 karakter"
        />
        <Input
          label="Konfirmasi Password Baru"
          type="password"
          name="confirmNewPassword"
          value={confirmNewPassword}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          required
          autoComplete="new-password"
        />
        <div className="flex justify-end pt-2">
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? 'Mengubah...' : 'Ubah Password'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// =============================================================================

function Field({
  label,
  value,
  mono = false,
  children,
}: {
  label: string;
  value?: string;
  mono?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="border-b border-gray-100 pb-3">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`mt-1 text-sm text-gray-800 ${mono ? 'font-mono' : ''}`}>
        {children ?? value}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color = (() => {
    switch (status) {
      case 'Aktif':
        return 'bg-green-100 text-green-800';
      case 'MenungguApproval':
      case 'MenungguVerifikasi':
        return 'bg-yellow-100 text-yellow-800';
      case 'Ditangguhkan':
        return 'bg-orange-100 text-orange-800';
      case 'Ditolak':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  })();
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${color}`}>
      {status}
    </span>
  );
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}
